import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { createPortal } from 'react-dom';

const RECAPTCHA_SITE_KEY = '6LdjloErAAAAAH5IP69fZ1k6STJnfKqk9fvetErA';

const AuthModal = ({ isOpen, onClose, initialTab = 'login' }) => {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [signupData, setSignupData] = useState({ 
    username: '', 
    password: '', 
    password_confirm: '', 
    email: '', 
    first_name: '', 
    last_name: '',
    phone: '',
    address: '',
    date_of_birth: ''
  });
  const [loginError, setLoginError] = useState('');
  const [signupError, setSignupError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);

  // Lock scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, isOpen]);

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    setLoginError('');
  };

  const handleSignupChange = (e) => {
    setSignupData({ ...signupData, [e.target.name]: e.target.value });
    setSignupError('');
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    const result = await login(loginData.username, loginData.password);
    if (!result.success) {
      // Convert error object to string if needed
      const errorMessage = typeof result.error === 'object' 
        ? Object.values(result.error).join(', ') 
        : result.error;
      setLoginError(errorMessage);
    } else {
      onClose();
    }
    setLoading(false);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSignupError('');
    if (!recaptchaValue) {
      setSignupError(t('auth.recaptchaRequired'));
      setLoading(false);
      return;
    }
    if (signupData.password !== signupData.password_confirm) {
      setSignupError(t('auth.passwordsDontMatch') || 'Passwords do not match.');
      setLoading(false);
      return;
    }
    
    // Automatically set user_type to 'patient' for all signups
    const registrationData = { 
      ...signupData, 
      user_type: 'patient',
      recaptcha: recaptchaValue 
    };
    
    const result = await register(registrationData);
    if (!result.success) {
      // Convert error object to string if needed
      const errorMessage = typeof result.error === 'object' 
        ? Object.values(result.error).join(', ') 
        : result.error;
      setSignupError(errorMessage);
    } else {
      onClose();
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg animate-fadeInUp max-h-[90vh] overflow-y-auto scrollbar-hide" style={{ position: 'relative', margin: 'auto' }}>
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 text-2xl z-20 bg-white dark:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          &times;
        </button>
        
        {/* Header */}
        <div className="text-center pt-6 pb-4">
          <h2 className="text-2xl font-playfair font-bold text-gray-800 dark:text-white">
            {activeTab === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {activeTab === 'login' ? t('auth.loginSubtitle') : t('auth.signupSubtitle')}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <button
            className={`px-6 py-3 font-semibold rounded-lg focus:outline-none transition-all duration-300 ${
              activeTab === 'login' 
                ? 'bg-tiffany text-white shadow-lg' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            onClick={() => setActiveTab('login')}
          >
            {t('auth.login')}
          </button>
          <button
            className={`px-6 py-3 font-semibold rounded-lg focus:outline-none transition-all duration-300 ml-2 ${
              activeTab === 'signup' 
                ? 'bg-tiffany text-white shadow-lg' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            onClick={() => setActiveTab('signup')}
          >
            {t('auth.signup')}
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {activeTab === 'login' && (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('auth.username')}</label>
                <input
                  type="text"
                  name="username"
                  value={loginData.username}
                  onChange={handleLoginChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  minLength={3}
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('auth.password')}</label>
                <input
                  type="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  minLength={6}
                  autoComplete="current-password"
                />
              </div>
              {loginError && (
                <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                  {loginError}
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-tiffany text-white py-3 rounded-lg hover:bg-tiffany-dark transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? t('common.loading') : t('auth.login')}
              </button>
            </form>
          )}

          {activeTab === 'signup' && (
            <form onSubmit={handleSignupSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.firstName')}</label>
                  <input
                    type="text"
                    name="first_name"
                    value={signupData.first_name}
                    onChange={handleSignupChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.lastName')}</label>
                  <input
                    type="text"
                    name="last_name"
                    value={signupData.last_name}
                    onChange={handleSignupChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.username')}</label>
                <input
                  type="text"
                  name="username"
                  value={signupData.username}
                  onChange={handleSignupChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                  required
                  minLength={3}
                  autoComplete="username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={signupData.email}
                  onChange={handleSignupChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                  required
                  autoComplete="email"
                />
              </div>

              {/* Contact Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.phone') || 'Phone Number'}</label>
                <input
                  type="tel"
                  name="phone"
                  value={signupData.phone}
                  onChange={handleSignupChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                  autoComplete="tel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.address') || 'Address'}</label>
                <textarea
                  name="address"
                  value={signupData.address}
                  onChange={handleSignupChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Enter your full address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.dateOfBirth') || 'Date of Birth'}</label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={signupData.date_of_birth}
                  onChange={handleSignupChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                />
              </div>

              {/* Password Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
                <input
                  type="password"
                  name="password"
                  value={signupData.password}
                  onChange={handleSignupChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.confirmPassword') || 'Confirm Password'}</label>
                <input
                  type="password"
                  name="password_confirm"
                  value={signupData.password_confirm}
                  onChange={handleSignupChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tiffany focus:border-transparent transition-all duration-300"
                  required
                  minLength={6}
                />
              </div>

              {/* Google reCAPTCHA */}
              <div className="flex justify-center">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={setRecaptchaValue}
                />
              </div>

              {signupError && (
                <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                  {signupError}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-tiffany text-white py-3 rounded-lg hover:bg-tiffany-dark transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? t('common.loading') : t('auth.signup')}
              </button>


            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AuthModal; 